import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { InlineField } from "@/components/ui/inline-edit"
import { useInlineRecord } from "@/hooks/use-inline-record"
import type { PageProps } from "@/types/inertia"

type Section = {
  id: number
  name: string
  kind: "venue" | "system" | "custom"
  position: number
  photo_count: number
  video_count: number
  site_edit_href: string | null
}

type Props = PageProps & {
  sections: Section[]
  unfiled_photo_count: number
  unfiled_video_count: number
}

const DESCRIPTION =
  "The folders behind the section menus on Photos and Videos. Add as many as you need."

function contents(section: Section) {
  const parts: string[] = []
  if (section.photo_count > 0) {
    parts.push(`${section.photo_count} photo${section.photo_count === 1 ? "" : "s"}`)
  }
  if (section.video_count > 0) {
    parts.push(`${section.video_count} video${section.video_count === 1 ? "" : "s"}`)
  }
  return parts.length > 0 ? parts.join(" · ") : "Empty"
}

function SectionCard({ section, sections }: { section: Section; sections: Section[] }) {
  const [moveTo, setMoveTo] = React.useState("")

  // A venue's folder follows its location, so those two boxes are read-only
  // here and nothing about them can be committed.
  const locked = section.kind === "venue"
  const holdings = section.photo_count + section.video_count

  const fields = useInlineRecord({
    record: { name: section.name, position: String(section.position) },
    url: `/admin/photos/sections/${section.id}`,
    param: "media_section",
    enabled: !locked,
  })
  const v = fields.values
  const nameState = fields.state("name")
  const positionState = fields.state("position", { serialize: (value) => Number(value) })

  function remove() {
    const fate =
      holdings === 0
        ? ""
        : moveTo
          ? ` Its ${holdings} item${holdings === 1 ? "" : "s"} will move to “${
              sections.find((s) => String(s.id) === moveTo)?.name
            }”.`
          : ` Its ${holdings} item${holdings === 1 ? "" : "s"} will be left unfiled — nothing is deleted.`

    if (!window.confirm(`Delete the “${section.name}” section?${fate}`)) return
    router.delete(`/admin/photos/sections/${section.id}`, {
      data: moveTo ? { move_to: moveTo } : {},
    })
  }

  return (
    <div className="rounded-lg border border-hairline bg-surface p-5">
      <div className="grid gap-4 sm:grid-cols-[1fr_6rem]">
        <InlineField label="Section name" htmlFor={`name_${section.id}`} state={nameState}>
          <Input
            id={`name_${section.id}`}
            value={v.name}
            onChange={(e) => fields.set("name", e.target.value)}
            onKeyDown={nameState.keys}
            disabled={locked}
          />
        </InlineField>
        <InlineField label="Order" htmlFor={`position_${section.id}`} state={positionState}>
          <Input
            id={`position_${section.id}`}
            type="number"
            min={0}
            value={v.position}
            onChange={(e) => fields.set("position", e.target.value)}
            onKeyDown={positionState.keys}
            disabled={locked}
          />
        </InlineField>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        {section.kind === "venue" && <Badge tone="muted">Location</Badge>}
        {section.kind === "system" && <Badge tone="muted">Built-in</Badge>}
        <span className="text-sm text-ink-muted">{contents(section)}</span>
        <Link href={`/admin/photos?section=${section.id}`} className="text-sm">
          View photos →
        </Link>
      </div>

      {locked && (
        <p className="mt-3 text-sm text-ink-muted">
          This section belongs to a location — rename or remove it by{" "}
          {section.site_edit_href ? (
            <Link href={section.site_edit_href}>editing the location</Link>
          ) : (
            "editing the location"
          )}
          .
        </p>
      )}
      {section.kind === "system" && (
        <p className="mt-3 text-sm text-ink-muted">
          Built in — rename it to whatever suits you, but it can&rsquo;t be deleted: new uploads are
          filed here automatically.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {section.kind === "custom" && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {holdings > 0 && (
              <Select
                id={`move_to_${section.id}`}
                aria-label={`Where to move the contents of ${section.name}`}
                value={moveTo}
                onChange={(e) => setMoveTo(e.target.value)}
                className="w-56"
              >
                <option value="">Leave contents unfiled</option>
                {sections
                  .filter((s) => s.id !== section.id)
                  .map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      Move contents to {s.name}
                    </option>
                  ))}
              </Select>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-danger-display"
              onClick={remove}
            >
              Delete section
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function AddSection() {
  const [name, setName] = React.useState("")
  const [saving, setSaving] = React.useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    router.post(
      "/admin/photos/sections",
      { media_section: { name } },
      { onSuccess: () => setName(""), onFinish: () => setSaving(false) },
    )
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-dashed border-hairline p-5">
      <div className="space-y-1.5">
        <label htmlFor="new_section_name">New section name</label>
        <Input
          id="new_section_name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g. Blog art"
        />
      </div>
      <div className="mt-4">
        <Button type="submit" variant="secondary" size="sm" disabled={saving}>
          Add section
        </Button>
      </div>
    </form>
  )
}

// The media library's folders. Photos and videos share them, so a section
// made here shows up in both section menus.
export default function MediaSectionsIndex() {
  const { props } = usePage<Props>()
  const flash = props.flash
  const unfiled = props.unfiled_photo_count + props.unfiled_video_count

  return (
    <AdminShell>
      <Head title="Media Sections">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Media Sections" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>

      <PageHeader
        title="Media sections"
        description={DESCRIPTION}
        actions={
          <Button variant="secondary" asChild>
            <Link href="/admin/photos">Back to photos</Link>
          </Button>
        }
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>}

      <p className="mt-6 max-w-3xl text-sm text-ink-muted">
        Every location gets a section automatically. The built-in ones catch uploads that arrive on
        their own — article covers land in Articles, and images made in Image Studio land in AI
        images. Everything else is yours to organise however you like.
      </p>

      <div className="mt-6 flex max-w-3xl flex-col gap-4">
        {props.sections.map((section) => (
          <SectionCard key={section.id} section={section} sections={props.sections} />
        ))}
        <AddSection />

        {unfiled > 0 && (
          <p className="text-sm text-ink-muted">
            {unfiled} item{unfiled === 1 ? " is" : "s are"} in no section at all.{" "}
            <Link href="/admin/photos?section=none">Review them →</Link>
          </p>
        )}
      </div>
    </AdminShell>
  )
}
